import { ICommandBase } from '@/commands/interface/iCommandBase';
import { MultiCommand } from '@/commands/multiCommand';
import { Const } from '@/const';

export class UndoRedoManager
{
    // コールバック
    private canUndoChangeCallbacks : Array<() => void> = [];
    private canRedoChangeCallbacks : Array<() => void> = [];

    public registerUndoChangeCallback(callback : () => void)
    {
        this.canUndoChangeCallbacks.push(callback);
    }

    public clearUndoChangeCallbacks() : void
    {
        this.canUndoChangeCallbacks = [];
    }

    private doCanUndoChangeCallbacks()
    {
        for(var i = 0; i < this.canUndoChangeCallbacks.length; ++i)
        {
            this.canUndoChangeCallbacks[i]();
        }
    }

    public registerRedoChangeCallback(callback : () => void)
    {
        this.canRedoChangeCallbacks.push(callback);
    }

    public clearRedoChangeCallbacks() : void
    {
        this.canRedoChangeCallbacks = [];
    }

    private doCanRedoChangeCallbacks()
    {
        for(var i = 0; i < this.canRedoChangeCallbacks.length; ++i)
        {
            this.canRedoChangeCallbacks[i]();
        }
    }

    // スタック
    private undoStack : Array<ICommandBase> = [];
    private redoStack : Array<ICommandBase> = [];

    // アンドゥ追加
    private addUndo(command : ICommandBase)
    {
        this.undoStack.push(command);

        // 古いスタックを削除する
        while(this.undoStack.length > Const.UNDO_STACK_SIZE)
        {
            this.undoStack.splice(0, 1);
        }

        // アンドゥ可能か更新
        this.canUndo = this.undoStack.length > 0;
    }

    // リドゥ追加
    private addRedo(command : ICommandBase)
    {
        this.redoStack.push(command);

        // 古いスタックを削除する
        while(this.redoStack.length > Const.UNDO_STACK_SIZE)
        {
            this.redoStack.splice(0, 1);
        }

        // リドゥ可能か更新
        this.canRedo = this.redoStack.length > 0;
    }

    // マルチコマンド
    private multiCommand : MultiCommand = new MultiCommand();

    public get multiCommandCount() { return this.multiCommand.commandCount; }

    // アンドゥ可能か？
    private _canUndo : boolean = false;
    public get canUndo() : boolean { return this._canUndo; }
    public set canUndo(canUndo : boolean)
    {
        if(this._canUndo != canUndo)
        {
            this._canUndo = canUndo;
            this.doCanUndoChangeCallbacks();
        }
    }

    // リドゥ可能か？
    private _canRedo : boolean = false;
    public get canRedo() : boolean { return this._canRedo; }
    public set canRedo(canRedo : boolean)
    {
        if(this._canRedo != canRedo)
        {
            this._canRedo = canRedo;
            this.doCanRedoChangeCallbacks();
        }
    }

    // アンドゥ・リドゥ履歴を初期化
    public init() : void
    {
        this.undoStack = [];
        this.redoStack = [];
        this.canUndo = false;
        this.canRedo = false;
    }

    // コマンドを実行し、アンドゥ履歴を残す
    public do(command : ICommandBase) : void
    {
        // マルチコマンドがあった場合はfixする
        this.fixMultiCommand();

        // アンドゥを蓄積する
        this.addUndo(command);

        // コマンドを実行する
        command.do();

        // リドゥをクリアする
        this.redoStack = [];
        // リドゥ可能か更新
        this.canRedo = this.redoStack.length > 0;
    }

    // アンドゥを行い、一つ前の状態に戻す
    public undo() : void
    {
        // マルチコマンドがあった場合はfixする
        this.fixMultiCommand();

        if(this.undoStack.length > 0)
        {
            // 最新のコマンドを取り出す
            var command = this.undoStack.pop();
            if(command == null) { return; }
            // アンドゥ可能か更新
            this.canUndo = this.undoStack.length > 0;

            // コマンドをアンドゥする
            command.undo();

            // リドゥを蓄積する
            this.addRedo(command);
        }
    }

    // リドゥを行い、一つ前の状態に戻す
    public redo() : void
    {
        // マルチコマンドがあった場合はfixする
        this.fixMultiCommand();
        
        if(this.redoStack.length > 0)
        {
            // 最新のコマンドを取り出す
            var command = this.redoStack.pop();
            if(command == null) { return; }
            // リドゥ可能か更新
            this.canRedo = this.redoStack.length > 0;

            // コマンドをリドゥする
            command.redo();

            // アンドゥを蓄積する
            this.addUndo(command);
        }
    }

    // マルチコマンド対応
    public doAsMultiCommand(command : ICommandBase, isFinish : boolean = false)
    {
        // マルチコマンドに追加する
        this.multiCommand.addCommand(command);

        // コマンドを実行する
        command.do();
        
        if(isFinish)
        {
            this.fixMultiCommand();
        }
    }

    // マルチコマンドのフィックス
    public fixMultiCommand()
    {
        if(this.multiCommand.commandCount > 0)
        {
            // マルチコマンドをアンドゥスタックに登録する
            this.addUndo(this.multiCommand);

            // リドゥをクリアする
            this.redoStack = [];
            // リドゥ可能か更新
            this.canRedo = this.redoStack.length > 0;

            // 新しいマルチコマンドを作成してメンバにセット
            this.multiCommand = new MultiCommand();
        }
    }
}